import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ShoppingBag, ArrowRight, AlertTriangle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useCart } from "../lib/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

const CheckoutSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState(() =>
    sessionId
      ? { state: "polling", message: "Confirming your payment..." }
      : { state: "error", message: "Missing session id." }
  );
  const [details, setDetails] = useState(null);
  const cartClearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus({ state: "success", message: "Payment successful." });
          if (!cartClearedRef.current) {
            cartClearedRef.current = true;
            clear();
          }
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired", message: "This checkout session expired." });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus({
            state: "timeout",
            message: "We didn't get a confirmation in time. Please check your email.",
          });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || "Unable to verify payment.";
        setStatus({ state: "error", message: typeof detail === "string" ? detail : "Error" });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  const isPolling = status.state === "polling";
  const isSuccess = status.state === "success";
  const isErrorish = ["error", "timeout", "expired"].includes(status.state);

  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />
      <section className="pt-32 md:pt-40 pb-24 min-h-[80vh]">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="checkout-status"
            className="border border-amber-400/30 bg-[#0a0a0a] p-8 md:p-12"
          >
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
              — AfroAm Store · Checkout
            </div>

            {isPolling && (
              <>
                <Loader2 size={42} className="text-amber-400 animate-spin mb-6" />
                <h1 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.9]">
                  Confirming your <span className="text-amber-400">order...</span>
                </h1>
                <p className="text-neutral-400 mt-5 max-w-xl leading-relaxed">
                  Hold tight — we&apos;re verifying your payment with Stripe.
                </p>
              </>
            )}

            {isSuccess && (
              <>
                <CheckCircle2 size={42} className="text-amber-400 mb-6" />
                <h1 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.9]">
                  Order
                  <br />
                  <span className="text-amber-400">confirmed.</span>
                </h1>
                <p className="text-neutral-300 mt-6 max-w-2xl leading-relaxed">
                  Thanks for backing the movement. We&apos;ve received your payment of{" "}
                  <span className="text-white">
                    ${details?.amount_total?.toFixed(2)} {(details?.currency || "usd").toUpperCase()}
                  </span>
                  . A confirmation has been sent — our team will reach out shortly with shipping details.
                </p>
                {details?.items?.length > 0 && (
                  <div className="mt-8 border border-neutral-900 divide-y divide-neutral-900">
                    {details.items.map((i, idx) => (
                      <div
                        key={`${i.product_id}-${idx}`}
                        className="flex items-center justify-between px-5 py-4"
                        data-testid={`success-item-${i.product_id}`}
                      >
                        <div>
                          <div className="text-sm text-white">{i.name}</div>
                          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                            Qty {i.quantity} · ${i.unit_price?.toFixed(2)} each
                          </div>
                        </div>
                        <div className="text-sm text-white">${i.line_total?.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/store"
                    data-testid="success-continue-shopping"
                    className="inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                  >
                    <ShoppingBag size={14} /> Continue Shopping
                  </Link>
                  <Link
                    to="/"
                    data-testid="success-back-home"
                    className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                  >
                    Back Home <ArrowRight size={14} />
                  </Link>
                </div>
              </>
            )}

            {isErrorish && (
              <>
                {status.state === "expired" ? (
                  <AlertTriangle size={42} className="text-amber-400 mb-6" />
                ) : (
                  <XCircle size={42} className="text-red-400 mb-6" />
                )}
                <h1 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.9]">
                  Payment <span className="text-amber-400">unverified.</span>
                </h1>
                <p className="text-neutral-300 mt-6 max-w-2xl leading-relaxed">{status.message}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/store"
                    className="inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                  >
                    Back to Store
                  </Link>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
